/**
 * RTL Scanner — Raw Flow source.
 *
 * Maps the flow engine's enriched tape (`GET /api/flow-engine/dataset` → `enriched_tape`,
 * fetched through services/flowEngineService) onto the same tile shape the imbalance view
 * renders, and exposes every field on those rows as a filterable dimension so the scanner's
 * custom filters can be built from them.
 *
 * The imbalance view's data-integrity rules carry over unchanged: nothing is invented. Fields the
 * tape does not carry (relative volume, horizon) stay null and render as "Not Available"; the two
 * derived values remain moneyness and colour, computed by the same helpers used for the pulse
 * tiles so both sources agree on what green and red mean.
 */

import type { EnrichedTapeRow } from '../types/flowEngineDataset';
import { verdictFromTapeRow } from './flowVerdictLabels';
import {
  computeColor,
  computeFlowLabel,
  computeMoneyness,
  MoneynessBucket,
  ScannerTile,
  TileFlow,
  TileSide,
} from './rtlScannerTiles';

// ── field registry ────────────────────────────────────────────────────────────

export type FlowFieldType = 'number' | 'text' | 'enum';

export interface FlowFieldDef {
  key: string;
  label: string;
  type: FlowFieldType;
  /** Known values for enum fields; the picker still accepts free text. */
  options?: string[];
}

/**
 * Every field the enriched tape carries, in display order. This is the source of truth for the
 * custom filter builder and for the extra Raw Flow table columns.
 */
export const FLOW_FIELDS: FlowFieldDef[] = [
  { key: 'ticker', label: 'Ticker', type: 'text' },
  { key: 'contract', label: 'Contract', type: 'text' },
  { key: 'option_type', label: 'Option type', type: 'enum', options: ['C', 'P'] },
  { key: 'side', label: 'Tape side', type: 'enum', options: ['Ask', 'Bid', 'Mid'] },
  { key: 'strike', label: 'Strike', type: 'number' },
  { key: 'expiry', label: 'Expiry', type: 'text' },
  { key: 'dte', label: 'DTE', type: 'number' },
  { key: 'premium', label: 'Premium', type: 'number' },
  { key: 'size', label: 'Print size', type: 'number' },
  { key: 'volume', label: 'Volume', type: 'number' },
  { key: 'oi', label: 'Open interest', type: 'number' },
  { key: 'vol_oi_ratio', label: 'Vol / OI', type: 'number' },
  { key: 'spot', label: 'Spot', type: 'number' },
  { key: 'stock', label: 'Stock', type: 'number' },
  { key: 'bid', label: 'Bid', type: 'number' },
  { key: 'ask', label: 'Ask', type: 'number' },
  { key: 'delta', label: 'Delta', type: 'number' },
  { key: 'gamma', label: 'Gamma', type: 'number' },
  { key: 'theta', label: 'Theta', type: 'number' },
  { key: 'vega', label: 'Vega', type: 'number' },
  { key: 'avg_iv', label: 'Avg IV', type: 'number' },
  { key: 'iv_rank', label: 'IV rank', type: 'number' },
  { key: 'moneyness', label: 'Moneyness', type: 'enum', options: ['ITM', 'ATM', 'OTM'] },
  { key: 'trade_style', label: 'Trade style', type: 'enum', options: ['regular', 'floor', 'multileg'] },
  { key: 'asset_class', label: 'Asset class', type: 'text' },
  { key: 'cap_tier', label: 'Cap tier', type: 'text' },
  { key: 'market_cap', label: 'Market cap', type: 'number' },
  { key: 'shares_outstanding', label: 'Shares outstanding', type: 'number' },
  { key: 'sic_code', label: 'SIC code', type: 'text' },
  { key: 'condition', label: 'Condition', type: 'text' },
  { key: 'trade_condition', label: 'Trade condition', type: 'number' },
  { key: 'trade_exchange', label: 'Exchange', type: 'number' },
  { key: 'trade_sequence', label: 'Sequence', type: 'number' },
  { key: 'timestamp', label: 'Print time', type: 'text' },
];

export const FLOW_FIELD_BY_KEY: Record<string, FlowFieldDef> = FLOW_FIELDS.reduce(
  (acc, f) => {
    acc[f.key] = f;
    return acc;
  },
  {} as Record<string, FlowFieldDef>,
);

// ── custom filters ────────────────────────────────────────────────────────────

export type FilterOp = 'gte' | 'lte' | 'eq' | 'ne' | 'contains';

export interface CustomFilter {
  /** Stable key for React lists; assigned by the caller. */
  id: string;
  field: string;
  op: FilterOp;
  value: string;
}

export const OP_LABELS: Record<FilterOp, string> = {
  gte: '≥',
  lte: '≤',
  eq: 'is',
  ne: 'is not',
  contains: 'contains',
};

export function opsForType(type: FlowFieldType): FilterOp[] {
  if (type === 'number') return ['gte', 'lte', 'eq', 'ne'];
  if (type === 'enum') return ['eq', 'ne'];
  return ['contains', 'eq', 'ne'];
}

/** Default operator when a field is picked — the one people reach for first per type. */
export function defaultOpForType(type: FlowFieldType): FilterOp {
  if (type === 'number') return 'gte';
  if (type === 'enum') return 'eq';
  return 'contains';
}

function matchesOne(raw: Record<string, unknown>, filter: CustomFilter): boolean {
  const def = FLOW_FIELD_BY_KEY[filter.field];
  if (!def) return true; // unknown field: do not silently drop every row
  const value = filter.value.trim();
  if (!value) return true; // an empty filter is not yet a constraint
  const cell = raw[filter.field];

  if (def.type === 'number') {
    const target = parseFloat(value);
    if (!Number.isFinite(target)) return true;
    const n = typeof cell === 'number' ? cell : parseFloat(String(cell ?? ''));
    // A row with no value for this field cannot satisfy a numeric comparison.
    if (!Number.isFinite(n)) return false;
    switch (filter.op) {
      case 'gte': return n >= target;
      case 'lte': return n <= target;
      case 'eq': return n === target;
      case 'ne': return n !== target;
      default: return true;
    }
  }

  const cellStr = String(cell ?? '').toLowerCase();
  const wanted = value.toLowerCase();
  switch (filter.op) {
    case 'contains': return cellStr.includes(wanted);
    case 'eq': return cellStr === wanted;
    case 'ne': return cellStr !== wanted;
    default: return true;
  }
}

/** All custom filters are ANDed, matching how the chip filters above them already behave. */
export function matchesCustomFilters(
  raw: Record<string, unknown> | undefined,
  filters: CustomFilter[],
): boolean {
  if (!filters.length) return true;
  if (!raw) return false; // Raw-Flow-only filters cannot match a row with no tape record
  return filters.every((f) => matchesOne(raw, f));
}

// ── tape row → scanner tile ───────────────────────────────────────────────────

/** `20260821` → `2026-08-21`; already-dashed values pass through. */
export function normalizeTapeExpiry(raw: unknown): string {
  const s = String(raw ?? '').trim();
  if (!s) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (/^\d{8}$/.test(s)) return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`;
  return s;
}

/**
 * Tape side → direction, using the platform's NBBO convention (see utils/flowPatternRecognition):
 * a print at the ask means the buyer lifted the offer, at the bid means the seller hit it. Mid and
 * anything unrecognised stay unknown rather than being guessed into a direction.
 */
export function tapeSideToFlow(side: unknown): TileFlow {
  const s = String(side ?? '').trim().toLowerCase();
  if (s === 'ask' || s === 'buy' || s === 'b') return 'BUY';
  if (s === 'bid' || s === 'sell' || s === 's') return 'SELL';
  return 'UNK';
}

function finiteOrNull(v: unknown): number | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : null;
}

/**
 * Map one enriched tape print onto a scanner tile. Returns null when the row lacks the identity a
 * contract needs (ticker, side, strike, expiry) — skipped rather than rendered half-known.
 */
export function tapeRowToTile(row: EnrichedTapeRow, index: number): ScannerTile | null {
  const t = String(row.ticker ?? '').trim().toUpperCase();
  if (!t) return null;

  const sideRaw = String(row.option_type ?? '').trim().toUpperCase();
  if (sideRaw !== 'C' && sideRaw !== 'P') return null;
  const side = sideRaw as TileSide;

  const strike = finiteOrNull(row.strike);
  if (strike == null) return null;

  const exp = normalizeTapeExpiry(row.expiry);
  if (!exp) return null;

  const flow = tapeSideToFlow(row.side ?? row.execution_side);
  const spot = finiteOrNull(row.spot) ?? finiteOrNull(row.stock);

  // Prefer the same spot-based derivation the imbalance tiles use so both sources read alike;
  // fall back to the engine's own bucket when spot is missing or unusable.
  let money: string;
  let mc: MoneynessBucket;
  const computed = computeMoneyness(spot && spot > 0 ? spot : null, strike, side);
  if (computed.mc !== 'NA') {
    money = computed.money;
    mc = computed.mc;
  } else {
    const bucket = String(row.moneyness ?? '').trim().toUpperCase();
    if (bucket === 'ITM' || bucket === 'ATM' || bucket === 'OTM') {
      money = bucket;
      mc = bucket as MoneynessBucket;
    } else {
      money = '----';
      mc = 'NA';
    }
  }

  return {
    t,
    rv: null, // the tape carries no relative volume
    side,
    strike,
    exp,
    dte: finiteOrNull(row.dte),
    prem: finiteOrNull(row.premium),
    flow,
    money,
    mc,
    fl: computeFlowLabel(flow, side),
    color: computeColor(flow, side),
    horizon_tml: null, // horizon is an imbalance-report concept
    cap: typeof row.cap_tier === 'string' && row.cap_tier ? row.cap_tier : undefined,
    id: `${row.contract ?? `${t}|${strike}|${exp}`}|${row.trade_sequence ?? ''}|${index}`,
    // A single print states its own side and structure outright, and carries open interest,
    // so all three readings are available here without any aggregation.
    verdict: verdictFromTapeRow(row as unknown as Record<string, unknown>),
    raw: row as unknown as Record<string, unknown>,
  };
}

export function tapeRowsToTiles(rows: EnrichedTapeRow[]): ScannerTile[] {
  const out: ScannerTile[] = [];
  rows.forEach((row, i) => {
    const tile = tapeRowToTile(row, i);
    if (tile) out.push(tile);
  });
  return out;
}
